'use client'

import Card from '@/components/ui/Card'
import { IconBuilding, IconCheck, IconWorld } from '@tabler/icons-react'
import { useState } from 'react'

interface RevokerPricingProps {
  locale: string
}

const freePlan = {
  id: 'free',
  name: { de: 'Free', en: 'Free' },
  description: { de: 'Alles für die Gesetzeskonformität', en: 'Everything for legal compliance' },
  price: 0,
  icon: IconCheck,
  features: {
    de: [
      'Widerrufsbutton auf Bestellseite',
      '2-Schritt-Bestätigungsprozess',
      'E-Mail-Benachrichtigungen',
      '6 Sprachen (DE, EN, FR, IT, ES, NL)',
      'Produkt-Ausnahmen',
      'Gast-Bestellungen Support',
      'Teilwiderruf möglich'
    ],
    en: [
      'Withdrawal button on order page',
      '2-step confirmation process',
      'Email notifications',
      '6 languages (DE, EN, FR, IT, ES, NL)',
      'Product exclusions',
      'Guest orders support',
      'Partial withdrawal possible'
    ]
  }
}

const proPlans = [
  {
    id: 'starter',
    name: { de: 'Starter', en: 'Starter' },
    description: { de: 'Perfekt für einen einzelnen Shop', en: 'Perfect for a single shop' },
    price: 79,
    currency: '$',
    sites: 1,
    icon: IconCheck,
    polarProductId: 'revoker-pro-starter',
    features: {
      de: [
        '1 Website-Lizenz',
        'Alle Free-Features',
        'Automatische Rückerstattungen',
        'Statistics Dashboard mit KPIs',
        'Fraud Detection & Risk Scoring',
        'Rules Engine für Automatisierung',
        'E-Mail Support'
      ],
      en: ['1 Site License', 'All Free features', 'Automatic refunds', 'Statistics dashboard with KPIs', 'Fraud detection & risk scoring', 'Rules engine for automation', 'Email Support']
    }
  },
  {
    id: 'business',
    name: { de: 'Business', en: 'Business' },
    description: { de: 'Ideal für wachsende Unternehmen', en: 'Ideal for growing businesses' },
    price: 149,
    currency: '$',
    sites: 5,
    icon: IconBuilding,
    polarProductId: 'revoker-pro-business',
    popular: true,
    features: {
      de: ['5 Website-Lizenzen', 'Alle Free-Features', 'Alle Starter-Features', 'Slack, Teams & Webhooks', 'REST API Zugang', 'DHL Return Labels', 'DATEV Export', 'Priority Support'],
      en: ['5 Site Licenses', 'All Free features', 'All Starter features', 'Slack, Teams & webhooks', 'REST API access', 'DHL return labels', 'DATEV export', 'Priority Support']
    }
  },
  {
    id: 'agency',
    name: { de: 'Agency', en: 'Agency' },
    description: { de: 'Für Agenturen & Entwickler', en: 'For agencies & developers' },
    price: 299,
    currency: '$',
    sites: -1,
    icon: IconWorld,
    polarProductId: 'revoker-pro-agency',
    features: {
      de: ['Unbegrenzte Lizenzen', 'Alle Business-Features', 'White-Label erlaubt', 'Früher Zugang zu Features', 'Dedicated Support'],
      en: ['Unlimited Licenses', 'All Business features', 'White-label allowed', 'Early feature access', 'Dedicated Support']
    }
  }
]

const RevokerPricing = ({ locale }: RevokerPricingProps) => {
  const [loading, setLoading] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const handleCheckout = async (productId: string) => {
    setLoading(productId)
    setError(null)

    try {
      const response = await fetch(`/api/checkout?product=${productId}`)
      const data = await response.json()

      if (!response.ok) {
        setError(data.error || (locale === 'en' ? 'Checkout failed' : 'Checkout fehlgeschlagen'))
        setLoading(null)
        return
      }

      if (data.url) {
        window.location.href = data.url
      } else {
        setError(locale === 'en' ? 'No checkout URL returned' : 'Keine Checkout-URL erhalten')
        setLoading(null)
      }
    } catch (err) {
      console.error('Checkout error:', err)
      setError(locale === 'en' ? 'An error occurred. Please try again.' : 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.')
      setLoading(null)
    }
  }

  return (
    <div className="col-span-full">
      {error && <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-xl text-red-300 text-center">{error}</div>}

      {/* Free Plan */}
      <div className="mb-8">
        <h3 className="text-white text-lg font-medium mb-4 text-center">{locale === 'en' ? 'Free Version' : 'Kostenlose Version'}</h3>
        <div className="max-w-md mx-auto">
          <Card className="p-6 lg:p-8 bg-KMRS-Dark border border-KMRS-Light">
            <div className="text-center mb-6">
              <freePlan.icon className="mx-auto mb-4 text-KMRS-Light" size={32} />
              <h3 className="text-white text-xl font-medium">Revoker {freePlan.name[locale as 'de' | 'en']}</h3>
              <p className="text-KMRS-Light text-sm mt-1">{freePlan.description[locale as 'de' | 'en']}</p>
            </div>

            <div className="text-center mb-6">
              <span className="text-4xl font-medium text-white">{locale === 'en' ? 'Free' : 'Kostenlos'}</span>
            </div>

            <ul className="space-y-3 mb-8">
              {freePlan.features[locale as 'de' | 'en'].map((feature, i) => (
                <li key={i} className="flex items-center gap-2 text-sm">
                  <IconCheck className="text-KMRS-Light" size={16} />
                  <span className="text-KMRS-Light">{feature}</span>
                </li>
              ))}
            </ul>

            <a
              href="https://wordpress.org/plugins/revoker-for-woocommerce/"
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full py-3 px-6 rounded-xl font-medium transition-colors text-center bg-KMRS-Light/20 text-white hover:bg-KMRS-Light/30"
            >
              {locale === 'en' ? 'Download Free' : 'Kostenlos herunterladen'}
            </a>

            <p className="text-center text-KMRS-Light text-xs mt-4">{locale === 'en' ? 'No credit card required' : 'Keine Kreditkarte erforderlich'}</p>
          </Card>
        </div>
      </div>

      {/* Pro Plans */}
      <div>
        <h3 className="text-white text-lg font-medium mb-4 text-center">{locale === 'en' ? 'Pro Plans' : 'Pro-Pläne'}</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {proPlans.map((plan) => (
            <Card key={plan.id} className={`p-6 lg:p-8 relative ${plan.popular ? 'bg-gradient-to-br from-accent/20 to-accent/5 border-2 border-accent' : 'bg-KMRS-Dark border border-KMRS-Light'}`}>
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-accent text-white text-xs font-medium rounded-full">{locale === 'en' ? 'MOST POPULAR' : 'BELIEBTESTE'}</div>
              )}

              <div className="text-center mb-6">
                <plan.icon className={`mx-auto mb-4 ${plan.popular ? 'text-accent' : 'text-KMRS-Light'}`} size={32} />
                <h3 className="text-white text-xl font-medium">{plan.name[locale as 'de' | 'en']}</h3>
                <p className="text-KMRS-Light text-sm mt-1">{plan.description[locale as 'de' | 'en']}</p>
              </div>

              <div className="text-center mb-6">
                <span className={`text-4xl font-medium ${plan.popular ? 'text-accent' : 'text-white'}`}>${plan.price}</span>
                <span className="text-KMRS-Light text-sm block">/ {locale === 'en' ? 'year' : 'Jahr'}</span>
              </div>

              <ul className="space-y-3 mb-8">
                {plan.features[locale as 'de' | 'en'].map((feature, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm">
                    <IconCheck className={plan.popular ? 'text-accent' : 'text-KMRS-Light'} size={16} />
                    <span className={plan.popular ? 'text-white' : 'text-KMRS-Light'}>{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleCheckout(plan.polarProductId)}
                disabled={loading === plan.polarProductId}
                className={`w-full py-3 px-6 rounded-xl font-medium transition-colors ${
                  plan.popular ? 'bg-accent text-white hover:bg-accent/90' : 'bg-KMRS-Light/20 text-white hover:bg-KMRS-Light/30'
                } ${loading === plan.polarProductId ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {loading === plan.polarProductId ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    {locale === 'en' ? 'Loading...' : 'Laden...'}
                  </span>
                ) : locale === 'en' ? (
                  'Buy Now'
                ) : (
                  'Jetzt kaufen'
                )}
              </button>

              {plan.sites > 0 && (
                <p className="text-center text-KMRS-Light text-xs mt-4">
                  {plan.sites === 1
                    ? locale === 'en'
                      ? `$${plan.price} per site`
                      : `$${plan.price} pro Seite`
                    : locale === 'en'
                      ? `$${(plan.price / plan.sites).toFixed(0)} per site`
                      : `$${(plan.price / plan.sites).toFixed(0)} pro Seite`}
                </p>
              )}
            </Card>
          ))}
        </div>
      </div>
    </div>
  )
}

export default RevokerPricing
